/**
 * Render sizes that are genuinely numbers rather than CSS classes: `<img width>`
 * / `<img height>` attributes and Ant Design icon `fontSize`, which is how antd
 * icons are scaled.
 *
 * Box geometry (panel width, radii, the avatar ring, the composer pill) lives in
 * `tailwind.config.js` as `ai-*` tokens instead — it belongs in the stylesheet.
 * These stay here because no Tailwind class can set an HTML width attribute.
 *
 * All values in px, taken from the Figma nodes listed in `src/ai_agent/README.md`.
 */

/** Oscar owl mark, per placement. */
export const OSCAR_MARK_SIZE = {
	launcher: 32,
	banner: 24,
	header: 32,
	hero: 56,
	/** Beside "Waiting for answers" (Figma 15122:53728). */
	loading: 29,
};

/** Icon glyphs, per role. */
export const OSCAR_ICON_SIZE = {
	/** Header action row: plus / comment / expand. */
	action: 20,
	close: 16,
	send: 16,
	chevron: 12,
	/** Mark inside the fullscreen header's ring (Figma 14998:125445). */
	framedMark: 26,
};

/**
 * The single horizontal inset for everything inside the panel's white card —
 * transcript rows, the empty state, the context bar, the connection strip, the
 * composer.
 *
 * It used to be four different values on one vertical axis: the transcript sat
 * at 36px, the empty state at 24px, the header at 16px and the composer at
 * 12px, so the greeting did not line up with the first message and the composer
 * lined up with neither. Misalignment is the first thing the eye finds, and it
 * was most of why the panel read as unfinished.
 *
 * `px-m` (16px) matches `OscarPanelHeader`, which sits just above the card,
 * so the title and the conversation share an edge.
 *
 * Written as a literal because Tailwind's scanner only sees literals — it reads
 * this file, so the class compiles even though nothing here is JSX.
 */
export const OSCAR_CONTENT_INSET = 'px-m';

/** Scroll-snap padding matching `OSCAR_CONTENT_INSET`, for full-bleed rows. */
export const OSCAR_CONTENT_SCROLL_INSET = 'scroll-px-m';

/**
 * Reading measure for agent prose in the drawer.
 *
 * The drawer is a column beside the app, so it is capped where a line stops
 * being comfortable to read. The fullscreen route is a different proposition —
 * the user has asked for the whole screen — so it passes its own measure and
 * takes the width it is given.
 */
export const OSCAR_READING_MEASURE = 'max-w-2xl';

/** Fullscreen: no cap. The point of going fullscreen is the space. */
export const OSCAR_FULL_MEASURE = 'max-w-none';

/**
 * The fullscreen recent-chats rail, as a share of the card. The default is
 * about the Figma 380px at a 1440 screen; the floor keeps titles legible at lg,
 * the ceiling keeps the conversation the main thing.
 */
export const OSCAR_RAIL_DEFAULT_PERCENT = 28;
export const OSCAR_RAIL_MIN_PERCENT = 20;
export const OSCAR_RAIL_MAX_PERCENT = 45;

/** Under the composer, wherever Oscar can be asked something. */
export const OSCAR_DISCLAIMER_COPY =
	'Oscar is AI and can make mistakes. Please double-check responses.';

/**
 * Copy for a turn the harness retried on a stronger model (an `escalated`
 * event). Deliberately says nothing about models: the reader only needs to know
 * the first pass was set aside, not which model wrote it. Wording is a
 * placeholder until design settles it.
 */
export const OSCAR_RETRY_COPY = {
	/** Live status while the retry has not shown anything yet. */
	status: 'taking a closer look',
	/** The line in the activity trace where the retry starts over. */
	restart: 'Started over for a closer look',
	/** Appended to the settled turn summary. */
	summary: 'took a second pass',
};
